import React, { useState, useEffect, ReactNode, useContext } from 'react'
import { EParamSortOrder } from '@unfoldrtech/portal-mic'
import { useDispatch, useSelector } from 'react-redux'
import {
  EChannelTypeGlobal,
  ECreativeStatus,
  EDealType,
  EReportColumnHeader,
  IDAReportAdGroupsCreativesTable,
  IDAReportCreative,
  IDAReportCreativesTable,
  IDOOHReportAdGroupsCreativesTable,
  IDOOHReportCreative,
  IDOOHReportCreativesTable,
} from '@unfoldrtech/portal-mic/dist/src/models/core'

import { TReportingFilters, TTranslationKey } from '../../../../models/types'
import { selectCreativesTableReportingFilters } from '../../../../store/reportingFilters'
import {
  selectRefreshTable,
  setRefreshTable,
} from '../../../../store/refreshTable'
import { ReportTableV3 } from '../../components/ReportTableV3'
import { TableDataCellImpressions } from '../../components/table-data-cell/TableDataCellImpressions'
import { TableDataCellCosts } from '../../components/table-data-cell/TableDataCellCosts'
import { useGetRetailerAndAdvertiserIds } from '../../hooks/use-get-retailer-and-advertiser-ids'
import { useGetDefaultSortBy } from '../../hooks/use-get-default-sort-by'
import { AdGroupContext, AppContext, CampaignContext } from '../../../../models'
import { TReportTableDataV3 } from '../../models/types/report-table-data-v3.type'
import { useGetDatasetKeyTableWhitelist } from '../../hooks/use-get-dataset-key-table-whitelist'
import {
  TDAAdGroupsCreativesTableHeaders,
  TDACreativesTableHeaders,
  TDOOHAdGroupsCreativesTableHeaders,
  TDOOHCreativesTableHeaders,
} from '../models'
import { TableDataCellCtr } from '../../components/table-data-cell/TableDataCellCtr'
import {
  TDatasetWhitelistKey,
  TGlobalTableDataType,
  TGlobalTables,
} from '../../models'
import { TableDataCellClicks } from '../../components/table-data-cell/TableDataCellClicks'
import { TableDataCellAvgRank } from '../../components/table-data-cell/TableDataCellAvgRank'
import { TableDataCellCarts } from '../../components/table-data-cell/TableDataCellCarts'
import { TableDataCellPurchases } from '../../components/table-data-cell/TableDataCellPurchases'
import { TableDataCellRevenue } from '../../components/table-data-cell/TableDataCellRevenue'
import { TableDataCellRoas } from '../../components/table-data-cell/TableDataCellRoas'
import { TableDataCellFrequency } from '../../components/table-data-cell/TableDataCellFrequency'
import { TableDataCellUniqueReach } from '../../components/table-data-cell/TableDataCellUniqueReach'
import { TableDataCellCpm } from '../../components/table-data-cell/TableDataCellCpm'
import { getCreativesEDatasetKey } from '../helpers/get-creatives-e-dataset-key'
import {
  getTableAndChartCreativesFn,
  type TCreativesTableHookParams,
} from '../helpers/get-table-and-chart-creatives-fn'
import { TableDataCellShareOfVoice } from '../../components/table-data-cell/TableDataCellShareOfVoice'
import { TableDataCellWinRate } from '../../components/table-data-cell/TableDataCellWinRate'
import { TableDataCellNewToBrandPurchases } from '../../components/table-data-cell/TableDataCellNewToBrandPurchases'
import { TableDataCellNewToBrandRevenue } from '../../components/table-data-cell/TableDataCellNewToBrandRevenue'
import { TableDataCellPlayouts } from '../../components/table-data-cell/TableDataCellPlayouts'
import useShowToast from '../../../../hooks/useShowToast'
import { ErrorToast } from '../../../ToastCard/ErrorToast'
import { TablePreloader } from '../../components/table-preloader'
import { TableDataCellAdGroupName } from '../../components/table-data-cell/TableDataCellAdGroupName'
import { TableDataCellCpc } from '../../components/table-data-cell/TableDataCellCpc'
import { TableDataCellName } from '../../components/table-data-cell/TableDataCellName'
import { TableDataCellPlatformType } from '../../components/table-data-cell/TableDataCellPlatformType'
import { TableDataCellCreative } from '../../components/table-data-cell/TableDataCellCreative'
import { TableDataCellStatusCreativeWithUpdate } from '../../components/table-data-cell/TableDataCellStatusCreativeWithUpdate'
import { setReportingData } from '../../../../store/reportingData'

type TCreativesTableHeaders = TDACreativesTableHeaders &
  TDAAdGroupsCreativesTableHeaders &
  TDOOHCreativesTableHeaders &
  TDOOHAdGroupsCreativesTableHeaders

type IReportCreativesTable =
  | IDAReportCreativesTable
  | IDAReportAdGroupsCreativesTable
  | IDOOHReportCreativesTable
  | IDOOHReportAdGroupsCreativesTable

export const ReportingAdGroupsTableWrapper = ({
  onTableDataChange,
  channelType,
}: {
  onTableDataChange: (creativesTableData: IReportCreativesTable) => void
  channelType?: EChannelTypeGlobal
}) => {
  const dispatch = useDispatch()
  const [errorUpdate, setErrorUpdate] = useState<TTranslationKey>()
  const [showUpdateErrorToast, setShowUpdateErrorToast] = useShowToast(false)

  const [appContext] = useContext(AppContext)
  const campaign = useContext(CampaignContext)
  const adGroup = useContext(AdGroupContext)

  const {
    startDate,
    endDate,
    timeWindow,
    tzZone,
    filterBy,
    filterOperation,
    filterValue,
    name,
    status,
  } = useSelector(selectCreativesTableReportingFilters)
  const refreshTable = useSelector(selectRefreshTable)
  const { retailerId, advertiserId } = useGetRetailerAndAdvertiserIds()

  const orgType = appContext.userOrg!.type
  const hookFn = getTableAndChartCreativesFn({
    orgType,
  })

  const getTableDataFn = hookFn.tableFn!

  const [sortBy, setSortBy] = useState<TCreativesTableHeaders>()
  const [sortOrder, setSortOrder] = useState<EParamSortOrder>()
  const [tableData, setTableData] = useState<TReportTableDataV3>()

  useGetDefaultSortBy({
    sortBy,
    sortOrder,
    // @ts-expect-error: fix in PORT-2535
    setSortBy,
    setSortOrder,
    orgType,
  })

  const {
    data: tableDataResponseObj,
    refetch,
    isFetching,
  } = getTableDataFn({
    retailerId,
    advertiserId,
    campaignId: campaign?.id,
    adGroupId: adGroup?.id,
    sortBy,
    timeWindow,
    startDate,
    endDate,
    sortOrder,
    tzZone,
    filterBy,
    filterOperation,
    filterValue,
    name: name ? encodeURI(name) : undefined,
    status,
    enabled: false,
  } as TCreativesTableHookParams)

  const onSortByChange = (sortColumn?: TGlobalTables) => {
    setSortBy(sortColumn as TCreativesTableHeaders)
  }

  const onSortOrderChange = (
    type: TReportingFilters['sortOrder'] = EParamSortOrder.Desc
  ) => {
    setSortOrder(type)
  }

  const tableDataResponse =
    tableDataResponseObj?.data.table || ({} as IReportCreativesTable)

  const whitelistDataList = useGetDatasetKeyTableWhitelist({
    key:
      (tableDataResponse.datasetKey as TDatasetWhitelistKey) ||
      getCreativesEDatasetKey(channelType, !!adGroup?.id),
  })

  const processTableDataResponse = (tableDataRes: IReportCreativesTable) => {
    const responseHeaders = Array.from(
      new Set(tableDataRes.rows.map((row) => Object.keys(row)).flat())
    )

    const whitelistData = whitelistDataList
      .map((wData) => {
        const whitelistItem = responseHeaders.find(
          (header) => wData.header === header
        )
        if (whitelistItem) {
          return wData
        }
        return undefined
      })
      .filter((wData) => wData) as Array<TGlobalTableDataType>

    const headers = whitelistData.map((wData) => wData.header)

    const processedTableData: TReportTableDataV3 = {
      columnIdList: headers,
      columnNameList: whitelistData.map((wData) => wData.translatedMetricName),
      columnNameTooltipList: whitelistData.map(
        (wData) => wData.translatedTooltipMetricName
      ),
      columnSpanList: headers.map((header) =>
        header === EReportColumnHeader.Name ? 4 : 2
      ),
      rowList: [],
    }

    tableDataRes.rows.forEach((row) => {
      const dataRow: Array<ReactNode | ReactNode[]> = []
      headers.forEach((header) => {
        let dataCell: ReactNode | ReactNode[]
        const dataValue = row[header as TCreativesTableHeaders]

        switch (header) {
          case EReportColumnHeader.Creative:
            dataCell = (
              <TableDataCellCreative
                dataValue={dataValue as IDAReportCreative | IDOOHReportCreative}
                channelType={channelType}
              />
            )
            break

          case EReportColumnHeader.Adgroup:
            dataCell = (
              <TableDataCellAdGroupName dataValue={dataValue?.toString()} />
            )
            break
          case EReportColumnHeader.Status:
            dataCell = (
              <TableDataCellStatusCreativeWithUpdate
                dataValue={dataValue?.toString() as ECreativeStatus}
                orgType={orgType}
                id={row.id}
                isDirectDeal={campaign.dealType !== EDealType.Programmatic}
                setErrorUpdateStatus={setErrorUpdate}
                setShowUpdateStatusErrorToast={setShowUpdateErrorToast}
              />
            )
            break
          case EReportColumnHeader.Placement:
            dataCell = <TableDataCellName dataValue={dataValue?.toString()} />
            break
          case EReportColumnHeader.PlatformType:
            dataCell = (
              <TableDataCellPlatformType dataValue={dataValue?.toString()} />
            )
            break
          case EReportColumnHeader.Impressions:
            dataCell = (
              <TableDataCellImpressions dataValue={Number(dataValue)} />
            )
            break
          case EReportColumnHeader.Clicks:
            dataCell = <TableDataCellClicks dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.Ctr:
            dataCell = <TableDataCellCtr dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.UniqueReach:
            dataCell = (
              <TableDataCellUniqueReach dataValue={Number(dataValue)} />
            )
            break
          case EReportColumnHeader.Frequency:
            dataCell = <TableDataCellFrequency dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.Costs:
            dataCell = <TableDataCellCosts dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.ShareOfVoice:
            dataCell = (
              <TableDataCellShareOfVoice dataValue={Number(dataValue)} />
            )
            break
          case EReportColumnHeader.WinRate:
            dataCell = <TableDataCellWinRate dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.Carts:
            dataCell = <TableDataCellCarts dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.PvCarts:
            dataCell = <TableDataCellCarts dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.Purchases:
            dataCell = <TableDataCellPurchases dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.PvPurchases:
            dataCell = <TableDataCellPurchases dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.Revenue:
            dataCell = <TableDataCellRevenue dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.PvRevenue:
            dataCell = <TableDataCellRevenue dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.NewToBrandPurchases:
            dataCell = (
              <TableDataCellNewToBrandPurchases dataValue={Number(dataValue)} />
            )
            break
          case EReportColumnHeader.NewToBrandRevenue:
            dataCell = (
              <TableDataCellNewToBrandRevenue dataValue={Number(dataValue)} />
            )
            break
          case EReportColumnHeader.Roas:
            dataCell = <TableDataCellRoas dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.Cpc:
            dataCell = <TableDataCellCpc dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.Cpm:
            dataCell = <TableDataCellCpm dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.AvgRank:
            dataCell = <TableDataCellAvgRank dataValue={Number(dataValue)} />
            break
          // DOOH Exclusive
          case EReportColumnHeader.Playouts:
            dataCell = <TableDataCellPlayouts dataValue={Number(dataValue)} />
            break

          case EReportColumnHeader.PvRoas:
            dataCell = <TableDataCellPlayouts dataValue={Number(dataValue)} />
            break

          default:
            dataCell = undefined
            break
        }

        if (dataCell) {
          dataRow.push(dataCell)
        }
      })

      processedTableData.rowList.push(dataRow)
    })

    return processedTableData
  }

  useEffect(() => {
    dispatch(setReportingData({ datasetKey: tableDataResponse.datasetKey }))
  }, [])

  useEffect(() => {
    if (sortBy && sortOrder && startDate && endDate) {
      refetch()
    }
  }, [sortBy, sortOrder, startDate, endDate])

  useEffect(() => {
    if (refreshTable) {
      refetch()
      dispatch(setRefreshTable({ refreshTable: false }))
    }
  }, [refreshTable])

  useEffect(() => {
    if (Object.keys(tableDataResponse).length > 0) {
      setTableData(processTableDataResponse(tableDataResponse))
      onTableDataChange(
        tableDataResponse as IDAReportCreativesTable &
          IDOOHReportCreativesTable &
          IDAReportAdGroupsCreativesTable &
          IDOOHReportAdGroupsCreativesTable
      )
    }
  }, [tableDataResponse])

  return (
    <>
      {tableData && (
        <ReportTableV3
          {...tableData}
          sortBy={sortBy}
          sortOrder={sortOrder}
          setSortBy={onSortByChange}
          setSortOrder={onSortOrderChange}
        />
      )}

      <ErrorToast
        show={showUpdateErrorToast}
        onClose={() => {
          setShowUpdateErrorToast(false)
          setErrorUpdate(undefined)
        }}
        translationKey={errorUpdate}
      />

      {isFetching && <TablePreloader />}
    </>
  )
}
